import { computed, Injectable, signal } from '@angular/core';

/** A document-query filter dimension and the server-side filter it emits. */
export interface DocumentFilterEntry {
  field: string;
  filter: DocumentFilter;
}

export interface DocumentFilter {
  type: string;
  name?: string;
  value?: string;
  values?: string[];
  operator?: 'AND' | 'OR';
  filters?: DocumentFilter[];
}

/** Selection state for the document-query filter sidebar. One signal per
 *  "Belege" dimension; `activeFilterEntries` turns the selections into the
 *  server-side filters folded into the grid's `serverSideFilter`. Route-scoped
 *  so it resets on (re-)entry. */
@Injectable()
export class DocumentQueryFilterStateService {
  readonly status = signal<string[] | null>(null);
  readonly constructionSite = signal<string[] | null>(null);
  readonly user = signal<string[] | null>(null);
  readonly salesOrganisation = signal<string[] | null>(null);
  readonly salesRepresentative = signal<string[] | null>(null);
  readonly project = signal<string[] | null>(null);
  readonly warehouse = signal<string[] | null>(null);
  readonly deliveryTour = signal<string[] | null>(null);
  readonly agent = signal<string[] | null>(null);
  readonly distributionType = signal<string[] | null>(null);

  /** The status field is document-type-prefixed (e.g. `sales_order.status`).
   *  The sidebar sets it from the current type so the emitted filter targets the
   *  right column. */
  readonly statusField = signal<string>('sales_order.status');

  /** Server-side filter entries for the currently-selected values. A single
   *  value emits `stringEquals`; multiple emit `stringSet`. */
  readonly activeFilterEntries = computed<DocumentFilterEntry[]>(() => {
    const entries: DocumentFilterEntry[] = [];

    const multiFilters: [string, string[] | null][] = [
      [this.statusField(), this.status()],
      ['customer.sub_number', this.constructionSite()],
      ['_user.user_full_name', this.user()],
      ['sales_organisation.sales_organisation', this.salesOrganisation()],
      ['sales_representative.sales_representative_number', this.salesRepresentative()],
      ['project.project', this.project()],
      ['warehouse.warehouse', this.warehouse()],
      ['delivery_tour.delivery_tour', this.deliveryTour()],
      ['agent.number', this.agent()],
      ['distribution_type.distribution_type', this.distributionType()],
    ];
    for (const [field, values] of multiFilters) {
      if (!values || values.length === 0) continue;
      const filter: DocumentFilter =
        values.length === 1
          ? { type: 'stringEquals', name: field, value: values[0] }
          : { type: 'stringSet', name: field, values };
      entries.push({ field, filter });
    }

    return entries;
  });

  /** True when any sidebar dimension has a selection. */
  readonly hasSelection = computed(() => this.activeFilterEntries().length > 0);

  /** Total number of selected values across all dimensions (for the badge). */
  readonly selectedValueCount = computed(
    () =>
      (this.status()?.length ?? 0) +
      (this.constructionSite()?.length ?? 0) +
      (this.user()?.length ?? 0) +
      (this.salesOrganisation()?.length ?? 0) +
      (this.salesRepresentative()?.length ?? 0) +
      (this.project()?.length ?? 0) +
      (this.warehouse()?.length ?? 0) +
      (this.deliveryTour()?.length ?? 0) +
      (this.agent()?.length ?? 0) +
      (this.distributionType()?.length ?? 0)
  );

  reset(): void {
    this.status.set(null);
    this.constructionSite.set(null);
    this.user.set(null);
    this.salesOrganisation.set(null);
    this.salesRepresentative.set(null);
    this.project.set(null);
    this.warehouse.set(null);
    this.deliveryTour.set(null);
    this.agent.set(null);
    this.distributionType.set(null);
  }
}
